import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, Phone, LogIn } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

const Login = () => {
  const navigate = useNavigate();
  const { login, register, loginWithGoogle } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '' });

  // Surface a notice passed from the Google OAuth callback (e.g. "awaiting
  // admin approval" / "request was not approved").
  React.useEffect(() => {
    try {
      const notice = sessionStorage.getItem('trv_auth_notice');
      if (notice) {
        toast.info(notice, { duration: 6000 });
        sessionStorage.removeItem('trv_auth_notice');
      }
    } catch (_) { /* noop */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        const res = await register(form.name, form.email, form.password, form.phone);
        if (res?.pending) {
          toast.success(res.message || 'Account created — awaiting admin approval.', { duration: 6000 });
          setIsRegister(false);
          setForm({ email: form.email, password: '', name: '', phone: '' });
          return;
        }
        toast.success('Account created!');
        navigate('/dashboard', { replace: true });
      } else {
        await login(form.email, form.password);
        toast.success('Welcome back!');
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/api/uploads/travcations-logo.png" alt="Travcations" className="h-16 mx-auto" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>

        <Card className="p-8 bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-100" data-testid="login-card">
          <h2 className="text-xl font-bold text-slate-900 mb-6 text-center" data-testid="auth-title">
            {isRegister ? 'Create Account' : 'Sign In'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      placeholder="John Doe"
                      required
                      data-testid="input-name"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Phone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="pl-10 h-12 rounded-xl"
                      placeholder="+91 98765 43210"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                  placeholder="you@company.com"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10 h-12 rounded-xl"
                  placeholder="Enter password"
                  required
                  data-testid="input-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="submit-btn"
            >
              <LogIn className="h-4 w-4 mr-2" />
              {loading ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
            </Button>
            {!isRegister && (
              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-teal-600 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            )}
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>

          {/* Google OAuth */}
          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            className="w-full h-12 rounded-xl border-2 border-slate-200 hover:border-slate-300 font-medium"
            data-testid="google-login-btn"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Toggle */}
          <p className="text-center text-sm text-slate-500 mt-6">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="ml-1 text-teal-600 font-semibold hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </Card>
      </div>
    </div>
  );
};

export default Login;
